import React, { useState, useRef, useEffect } from 'react';
import jump from 'jump.js';
import Collapsible from '~components/Collapsible';
import { easeInOutQuad } from '~utils/easing';
import usePrevious from '~utils/hooks/usePrevious';
import usePagination from '~utils/hooks/usePagination';
import { Nullable } from '~src/types';
import { SortingDirectionsType } from '~src/types';
import {
  Block,
  StyledTable,
  Th,
  Text,
  Td,
  Tr,
  OpenerText,
  SortingIcon,
  ShowMoreButton,
  ShowMoreButtonWrapper,
  ChevronUpIcon,
  CircleUpIcon,
} from './styledComponents';

export type TextItemConfig<DataType> = {
  field: keyof DataType;
  type: 'text';
  isSortable?: boolean;
  isOpener?: boolean;
  getThLabel?: (thArgs: { windowWidth: number }) => string;
  getTdLabel?: (tdArgs: { windowWidth: number; data: DataType }) => string;
};

export type CustomItemConfig<DataType> = {
  type: 'custom';
  render: (data: DataType) => React.ReactNode;
};

export type ColorGroup = {
  color: string;
  ids: (string | number)[];
};

export type TableConfig<DataType> = (
  | TextItemConfig<DataType>
  | CustomItemConfig<DataType>
)[];

export type Props<DataType extends Record<string, any>> = {
  className?: string;
  openerIcon?: 'chevron' | 'circle';
  config: TableConfig<DataType>;
  data: DataType[];
  sortField: keyof DataType;
  sortingDirection: SortingDirectionsType;
  onSortBtnClick: (
    field: keyof DataType,
    sortingDirection: SortingDirectionsType,
  ) => void;
  getId: (data: DataType) => string | number;
  renderExpandedComponent?: (data: DataType) => React.ReactNode;
  colorGroups?: ColorGroup[];
  expandAnimationDuration?: number;
  itemsPerPage?: number;
  shouldScrollToExpandedComponent?: boolean;
};

export const Table = <DataType extends Record<string, any>>({
  expandAnimationDuration = 500,
  itemsPerPage = 0,
  colorGroups,
  className,
  shouldScrollToExpandedComponent,
  config,
  data,
  sortField,
  sortingDirection,
  onSortBtnClick,
  renderExpandedComponent,
  getId,
  openerIcon,
}: Props<DataType>) => {
  const [expandedItemId, setExpandedItemId] = useState<
    Nullable<string | number>
  >(null);
  const [windowWidth, setWindowWidth] = useState<number>(0);

  let expandedItemRef = useRef<HTMLDivElement>(null);

  const {
    paginatedData,
    hasMoreItems,
    showMore,
    shownItemsCount,
  } = usePagination(data, itemsPerPage);

  const prevExpandedItemId = usePrevious(expandedItemId);

  const toggleExpandedId = (id: typeof expandedItemId) => {
    setExpandedItemId((prevId) => (id === prevId ? null : id));
  };

  useEffect(() => {
    const setWidth = () => {
      setWindowWidth(window.innerWidth);
    };

    setWidth();

    window.addEventListener('resize', setWidth);

    return () => {
      window.removeEventListener('resize', setWidth);
    };
  }, []);

  useEffect(() => {
    const isOpening = !prevExpandedItemId && !!expandedItemId;

    if (
      shouldScrollToExpandedComponent &&
      isOpening &&
      expandedItemRef.current
    ) {
      jump(expandedItemRef.current, {
        offset: -100,
        duration: expandAnimationDuration,
        easing: easeInOutQuad,
      });
    }
  }, [expandedItemId]);

  return (
    <div className={className}>
      <Block>
        <StyledTable>
          <thead>
            <tr>
              {config.map((item, i) => {
                const isTableSortedByHeader =
                  item.type === 'text' ? item.field === sortField : false;

                const direction = isTableSortedByHeader
                  ? sortingDirection
                  : 'desc';

                const oppositeDirection = direction === 'asc' ? 'desc' : 'asc';

                const headerName =
                  item.type === 'text' && item.getThLabel
                    ? item.getThLabel({ windowWidth })
                    : null;

                const isSortable =
                  item.type === 'text' ? item.isSortable : false;

                const handleHeaderClick = () => {
                  if (item.type === 'text') {
                    onSortBtnClick(item.field, oppositeDirection);
                  }
                };

                return (
                  <Th key={i}>
                    <Text
                      isCursorPointer={isSortable}
                      onClick={handleHeaderClick}
                    >
                      {headerName}{' '}
                      {isSortable && (
                        <SortingIcon
                          direction={direction}
                          color={isTableSortedByHeader ? '#49b070' : '#a9a9a8'}
                        />
                      )}
                    </Text>
                  </Th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {paginatedData.map((itemData, paginatedDataIndex) => {
              const id = getId(itemData);

              const isCollapsed = expandedItemId === id;
              const tableDataElems = [];

              const toggle = (e: React.MouseEvent) => {
                if (renderExpandedComponent) {
                  e.stopPropagation();
                  toggleExpandedId(id);
                }
              };

              config.forEach((configItem, i) => {
                if (configItem.type === 'text') {
                  let fieldData = configItem.getTdLabel
                    ? configItem.getTdLabel({ windowWidth, data: itemData })
                    : itemData[configItem.field];

                  // костыль: до 20 этажа - "Низкий", 20-40 "Средний", 40+ "Высокий"
                  if (configItem.field === 'floor') {
                    fieldData = fieldData < 20 ? 'Низкий'
                      : fieldData < 40 ? 'Средний'
                        : 'Высокий';
                  }

                  tableDataElems.push(
                    <Td key={i}>
                      {configItem.isOpener ? (
                        <OpenerText onClick={toggle}>{fieldData}</OpenerText>
                      ) : (
                        <span>{fieldData}</span>
                      )}
                    </Td>,
                  );
                }

                if (configItem.type === 'custom') {
                  tableDataElems.push(
                    <Td isContentCentered key={i}>
                      <span onClick={(e) => e.stopPropagation()}>
                        {' '}
                        {configItem.render(itemData)}
                      </span>
                    </Td>,
                  );
                }
              });

              if (openerIcon) {
                const iconProps = {
                  onClick: toggle,
                  isCollapsed,
                  expandAnimationDuration,
                };

                tableDataElems.push(
                  <Td key='opener' isContentCentered>
                    {openerIcon === 'chevron' && (
                      <ChevronUpIcon {...iconProps} />
                    )}
                    {openerIcon === 'circle' && <CircleUpIcon {...iconProps} />}
                  </Td>,
                );
              }

              let rowBgColor;

              if (colorGroups) {
                colorGroups.forEach(({ ids, color }) => {
                  if (ids.includes(getId(itemData))) {
                    rowBgColor = color;
                  }
                });
              }

              const isLast = paginatedDataIndex === paginatedData.length - 1;

              return (
                <React.Fragment key={id}>
                  <Tr
                    isCollapsible={!!renderExpandedComponent}
                    onClick={toggle}
                    bgColor={rowBgColor}
                    hasBottomBorder={isLast ? isCollapsed : true}
                  >
                    {tableDataElems}
                  </Tr>
                  {renderExpandedComponent && (
                    <Tr hasBottomBorder={isCollapsed}>
                      <td colSpan={tableDataElems.length}>
                        <Collapsible
                          collapsedRef={expandedItemRef}
                          animationDuration={expandAnimationDuration}
                          isCollapsed={isCollapsed}
                        >
                          {renderExpandedComponent(itemData)}
                        </Collapsible>
                      </td>
                    </Tr>
                  )}
                </React.Fragment>
              );
            })}
          </tbody>
        </StyledTable>
      </Block>
      {hasMoreItems && (
        <ShowMoreButtonWrapper>
          <ShowMoreButton onClick={showMore}>
            {shownItemsCount} из {data.length} показать ещё
          </ShowMoreButton>
        </ShowMoreButtonWrapper>
      )}
    </div>
  );
};
